import { inject } from '@angular/core';
import { AppFeatureActions } from '@app/app.actions';
import { selectStepValidityByStepName } from '@app/app.reducer';
import { FormName } from '@core/data-access/models/form/form.model';
import { OrderResponseModel } from '@core/data-access/models/save-order/order-response.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { ErrorHandlingService } from '@shared/error-handling/error-handling.service';
import { STEP_FORMS_DEBOUNCE_TIME } from '@shared/tokens';
import { CustomizationService } from '@store/customization/customization.service';
import { FormStatusPayloadModel, StepName, VisibilityPayloadModel } from '@store/step-forms/step.interface';
import {
  ActionTypes,
  SendOrder,
  SetFormValidity,
  SetOrderResponse,
  SetStepsVisibility
} from '@store/step-forms/steps-forms.actions';
import { StepsFormsService } from '@store/step-forms/steps-forms.service';
import { defaultTo, isEmpty } from 'lodash';
import { EMPTY, catchError, concatMap, debounceTime, map, of, switchMap } from 'rxjs';

export const sendOrder$ = createEffect(
  (
    actions$ = inject(Actions),
    stepFormsDebounceTime = inject(STEP_FORMS_DEBOUNCE_TIME, { optional: true }),
    stepsFormsService = inject(StepsFormsService),
    errorHandlingService = inject(ErrorHandlingService),
    customizationService = inject(CustomizationService)
  ) =>
    actions$.pipe(
      ofType<SendOrder>(ActionTypes.SEND_ORDER),
      debounceTime(stepFormsDebounceTime),
      switchMap(({ payload }) => {
        if (isEmpty(payload)) {
          return EMPTY;
        }

        return stepsFormsService.sendFinalOrder(payload).pipe(
          map((orderResponseData) => {
            const orderResponse = <OrderResponseModel>{
              ...orderResponseData.body,
              orderPaid: false
            };

            if (!orderResponse.success) {
              errorHandlingService.errorHandler(orderResponse);
            }

            customizationService.resetShoppingStartTime();

            return new SetOrderResponse(orderResponse);
          }),
          catchError(() => of(new SetOrderResponse({ error: 'fail' })))
        );
      })
    ),
  {
    functional: true
  }
);

export const setBuyerQuestionnaireStepVisibilityAndValidity = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(AppFeatureActions.buyerQuestionnaireFeatureAdded, AppFeatureActions.buyerQuestionnaireFeatureRemoved),
      concatLatestFrom(() => store.select(selectStepValidityByStepName(StepName.BuyerQuestionnaire))),
      concatMap(([{ type }, buyerQuestionnaireStepValidity]) => {
        const isVisible = type == AppFeatureActions.buyerQuestionnaireFeatureAdded.type;

        const visibilityPayload: VisibilityPayloadModel = {
          stepKey: StepName.BuyerQuestionnaire,
          visible: isVisible
        };

        const setFormValidity: FormStatusPayloadModel = {
          formInfo: [StepName.TicketAssignment, FormName.TicketHolder],
          valid: defaultTo(buyerQuestionnaireStepValidity?.valid, false)
        };

        return [new SetStepsVisibility([visibilityPayload]), new SetFormValidity(setFormValidity)];
      })
    ),
  { functional: true }
);

export const setTicketAssignmentStepVisibilityAndValidity = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(AppFeatureActions.ticketAssignmentFeatureAdded, AppFeatureActions.ticketAssignmentFeatureRemoved),
      concatLatestFrom(() => store.select(selectStepValidityByStepName(StepName.TicketAssignment))),
      concatMap(([{ type }, ticketAssignmentStepValidity]) => {
        const isVisible = type == AppFeatureActions.ticketAssignmentFeatureAdded.type;

        const visibilityPayload: VisibilityPayloadModel = {
          stepKey: StepName.TicketAssignment,
          visible: isVisible
        };

        const setFormValidity: FormStatusPayloadModel = {
          formInfo: [StepName.TicketAssignment, FormName.TicketHolder],
          valid: defaultTo(ticketAssignmentStepValidity?.valid, false)
        };

        return [new SetStepsVisibility([visibilityPayload]), new SetFormValidity(setFormValidity)];
      })
    ),
  { functional: true }
);
